import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import BlogSidebar from "./BlogSidebar";
import BlogComment from "./BlogComment";
import BlogCommentForm from "./BlogCommentForm";
import UserAvatar from "../Avatar/UserAvatar";
import blogService from "../../services/blogService";

export default function BlogDetailsInfo({ onBlogLoaded }) {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comments, setComments] = useState([]);

  const fetchComments = async (blogId) => {
    try {
      const response = await blogService.getComments(blogId);
      if (response && response.data) {
        setComments(response.data);
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
    }
  };

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null); // Reset error state
        const data = await blogService.getBlogBySlug(slug);

        if (data && data.success) {
          // Transform API data to match frontend format
          const transformedBlog = {
            ...data.data,
            tags: data.data.tags || [],
            content: {
              intro: data.data.content_intro,
              sections: (data.data.content_sections || []).map(section => ({
                heading: section.heading || '',
                content: section.content || '',
                images: section.images || []
              }))
            }
          };
          setBlog(transformedBlog);

          // Pass blog title to parent component for breadcrumb
          if (onBlogLoaded && data.data.title) {
            onBlogLoaded(data.data.title);
          }

          // Fetch comments for the blog
          if (data.data.id) {
            fetchComments(data.data.id);
          }
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching blog:', err);
        if (err.response && err.response.status === 404) {
          setNotFound(true);
        } else {
          setError('Unable to load blog. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
    }
  }, [slug, onBlogLoaded]);

  const handleCommentSubmitted = (newComment) => {
    // Optimistically add the new comment or refetch
    // Adding it to top of list since we sort by newest
    setComments(prevComments => [newComment, ...prevComments]);
  };

  // If blog not found, redirect to blogs page
  if (notFound) {
    return <Navigate to="/blogs" replace />;
  }

  // Loading state
  if (loading) {
    return (
      <section className="py-16 md:py-[130px]">
        <div className="theme-container w-full mx-auto">
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple"></div>
            <p className="text-white mt-4">Loading blog...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-16 md:py-[130px]">
        <div className="theme-container w-full mx-auto">
          <div className="text-center py-16">
            <h2 className="text-2xl font-semibold text-white mb-4">Error Loading Blog</h2>
            <p className="text-paragraph mb-8">{error}</p>
            <Link
              to="/blogs"
              className="inline-block px-6 py-3 bg-purple text-white rounded-lg hover:bg-purple/80 transition-colors"
            >
              Back to Blogs
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // No blog data
  if (!blog) {
    return null;
  }

  return (
    <section className="py-16 md:py-[130px]">
      <div className="theme-container w-full mx-auto grid grid-cols-8 lg:grid-cols-12 gap-y-16 lg:gap-y-0 lg:gap-x-[70px]">
        {/* <!-- blogs start  --> */}
        <div className="col-span-8">
          {/* <!-- single blog start --> */}
          <div className="w-full">
            <img
              src={blog.image}
              alt={blog.title}
              className="object-cover w-full rounded-lg"
            />

            {/* Blog Meta Information */}
            <div className="flex flex-wrap gap-4 items-center mt-6 mb-4">
              <div className="flex items-center gap-2">
                <UserAvatar className="w-10 h-10" />
                <span className="text-paragraph font-medium">{blog.author}</span>
              </div>
              <span className="text-paragraph">•</span>
              <span className="text-paragraph">{blog.date}</span>
              <span className="text-paragraph">•</span>
              <span className="text-paragraph">{blog.readTime}</span>
              <span className="text-paragraph">•</span>
              <div className="flex items-center gap-1">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 14.5C4.41 14.5 1.5 11.59 1.5 8C1.5 4.41 4.41 1.5 8 1.5C11.59 1.5 14.5 4.41 14.5 8C14.5 11.59 11.59 14.5 8 14.5ZM8 2.5C4.96 2.5 2.5 4.96 2.5 8C2.5 11.04 4.96 13.5 8 13.5C11.04 13.5 13.5 11.04 13.5 8C13.5 4.96 11.04 2.5 8 2.5Z" fill="#9CA3AF" />
                  <path d="M10.5 10.75C10.37 10.75 10.24 10.7 10.14 10.6L7.64 8.85C7.39 8.67 7.25 8.36 7.25 8.05V5C7.25 4.59 7.59 4.25 8 4.25C8.41 4.25 8.75 4.59 8.75 5V8.05L11.36 9.9C11.71 10.15 11.81 10.63 11.56 10.98C11.4 11.22 11.15 11.35 10.9 11.35L10.5 10.75Z" fill="#9CA3AF" />
                </svg>
                <span className="text-paragraph">{comments.length} Comments</span>
              </div>
            </div>

            <h1 className="text-24 sm:text-[30px] leading-[40px] tracking-tight text-white font-semibold mt-[30px]">
              {blog.title}
            </h1>

            <div className="flex mt-[22px] mb-2 gap-4 items-center">
              <svg
                className="hidden md:block flex-shrink-0"
                width="33"
                height="39"
                viewBox="0 0 33 39"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.4489 24.2727H20.1761L23.5682 28.6705L25.9886 31.5341L31.8011 39H25.6648L21.7102 33.9886L20.0227 31.6023L14.4489 24.2727ZM32.6364 18.5455C32.6364 22.3068 31.9318 25.5284 30.5227 28.2102C29.125 30.8807 27.2159 32.9261 24.7955 34.3466C22.3864 35.767 19.6534 36.4773 16.5966 36.4773C13.5398 36.4773 10.8011 35.767 8.38068 34.3466C5.97159 32.9148 4.0625 30.8636 2.65341 28.1932C1.25568 25.5114 0.556818 22.2955 0.556818 18.5455C0.556818 14.7841 1.25568 11.5682 2.65341 8.89773C4.0625 6.21591 5.97159 4.16477 8.38068 2.74432C10.8011 1.32386 13.5398 0.613635 16.5966 0.613635C19.6534 0.613635 22.3864 1.32386 24.7955 2.74432C27.2159 4.16477 29.125 6.21591 30.5227 8.89773C31.9318 11.5682 32.6364 14.7841 32.6364 18.5455ZM26.2784 18.5455C26.2784 15.8977 25.8636 13.6648 25.0341 11.8466C24.2159 10.017 23.0795 8.63636 21.625 7.70455C20.1705 6.76136 18.4943 6.28977 16.5966 6.28977C14.6989 6.28977 13.0227 6.76136 11.5682 7.70455C10.1136 8.63636 8.97159 10.017 8.14205 11.8466C7.32386 13.6648 6.91477 15.8977 6.91477 18.5455C6.91477 21.1932 7.32386 23.4318 8.14205 25.2614C8.97159 27.0795 10.1136 28.4602 11.5682 29.4034C13.0227 30.3352 14.6989 30.8011 16.5966 30.8011C18.4943 30.8011 20.1705 30.3352 21.625 29.4034C23.0795 28.4602 24.2159 27.0795 25.0341 25.2614C25.8636 23.4318 26.2784 21.1932 26.2784 18.5455Z"
                  fill="#2322f0"
                />
              </svg>

              <p className="flex-1 text-paragraph">
                {blog.description}
              </p>
            </div>

            {/* Main Content - Dynamic content from blog data */}
            <div className="prose prose-invert max-w-none">
              {blog.content && (
                <>
                  <p className="text-paragraph mb-[25px]">
                    {blog.content.intro}
                  </p>

                  {blog.content.sections.map((section, index) => (
                    <div key={index}>
                      <h2 className="text-24 sm:text-[30px] leading-[40px] tracking-tight text-white font-semibold mt-[30px]">
                        {section.heading}
                      </h2>
                      <p className="text-paragraph mt-5 mb-[25px]">
                        {section.content}
                      </p>

                      {/* Display section images if available */}
                      {section.images && section.images.length > 0 && (
                        <div className={`grid grid-cols-1 ${section.images.length > 1 ? 'md:grid-cols-2' : ''} gap-[30px] pt-6`}>
                          {section.images.map((imageUrl, imgIndex) => (
                            <div key={imgIndex} className="relative w-full overflow-hidden rounded-lg aspect-video">
                              <img
                                src={imageUrl}
                                alt={`${section.heading} ${imgIndex + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.src = '/assets/images/blog-1.png';
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            <div className="border-b border-purple/30 mt-8"></div>

            {/* Category and Tags */}
            <div className="flex pt-6 pb-6 flex-col sm:flex-row justify-between gap-6">
              <div>
                <h3 className="font-semibold text-18 text-white mb-3">
                  Category
                </h3>
                <span className="inline-block px-4 py-2 bg-purple/20 text-purple rounded-md text-sm">
                  {blog.category}
                </span>
              </div>

              <div className="flex-1 sm:ml-8">
                <h3 className="font-semibold text-18 text-white mb-3">
                  Tags
                </h3>
                <div className="flex gap-2.5 flex-wrap">
                  {blog.tags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/blogs?tag=${encodeURIComponent(tag)}`}
                      className="group overflow-hidden flex justify-center items-center relative text-sm border border-purple/30 rounded-md py-1.5 before:inline-block before:absolute before:z-0 before:w-full before:h-full before:bg-purple before:scale-x-0 group hover:before:scale-x-100 before:origin-right hover:before:origin-left before:transition-transform before:ease-out before:duration-300"
                    >
                      <span className="px-[18px] text-chip relative z-20 transition-colors ease-in-out duration-300 group-hover:text-white">
                        {tag}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-b border-purple/30"></div>

            {/* <!-- comments --> */}
            <BlogComment
              comments={comments}
              blogId={blog.id}
              onReplySubmitted={() => fetchComments(blog.id)}
            />
            {/* <!-- comment form  --> */}
            <BlogCommentForm
              blogId={blog.id}
              onCommentSubmitted={() => fetchComments(blog.id)}
            />
          </div>
        </div>
        {/* <!-- blogs end  --> */}
        {/* <!-- side bar start --> */}
        <BlogSidebar />
        {/* <!-- side bar end  --> */}
      </div>
    </section>
  );
}
